import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AxiosError } from 'axios';

import { PageLoader } from 'components/PageLoader';
import { codeSchema } from 'utils/validators';

import {
  acceptCounter,
  cancelBooking,
  getBooking,
  submitCompletionCode,
} from './api';
import { BookingScene } from './scene';

const COMPLETION_CODE_LENGTH = 4;
const completionFormSchema = z.object({
  code: codeSchema(COMPLETION_CODE_LENGTH),
});
export type CompletionFormValues = z.infer<typeof completionFormSchema>;

export default function Booking() {
  const { viewToken } = useParams<{ viewToken: string }>();
  const [data, setData] = useState<Kudable.BookingViewData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [acceptingCounter, setAcceptingCounter] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const form = useForm<CompletionFormValues>({
    resolver: zodResolver(completionFormSchema),
    mode: 'onBlur',
    defaultValues: { code: '' },
  });

  useEffect(() => {
    if (!viewToken) return;
    getBooking(viewToken)
      .then((response) => setData(response.data))
      .catch(() => setLoadError('This booking could not be found.'));
  }, [viewToken]);

  const handleSubmitCode = (values: CompletionFormValues) => {
    if (!viewToken) return;
    setSubmitError(null);
    return submitCompletionCode(viewToken, values.code.trim())
      .then(() => setConfirmed(true))
      .catch((requestError: AxiosError<{ error?: string }>) => {
        const code = requestError.response?.data?.error;
        if (code === 'wrong_code')
          setSubmitError("That code doesn't match. Please try again.");
        else if (code === 'already_complete')
          setSubmitError('This booking is already marked complete.');
        else if (code === 'too_many_attempts')
          setSubmitError('Too many wrong attempts. Try again in an hour.');
      });
  };

  const handleAcceptCounter = () => {
    if (!viewToken) return;
    setAcceptingCounter(true);
    acceptCounter(viewToken)
      .then((response) => setData(response.data))
      .catch(() =>
        setSubmitError('Could not accept the offer. Please try again.'),
      )
      .finally(() => setAcceptingCounter(false));
  };

  const handleConfirmCancel = () => {
    if (!viewToken) return;
    setCancelling(true);
    cancelBooking(viewToken)
      .then((response) => setData(response.data))
      .catch(() =>
        setSubmitError('Could not cancel the booking. Please try again.'),
      )
      .finally(() => {
        setCancelling(false);
        setShowCancelConfirm(false);
      });
  };

  if (loadError)
    return <div style={{ padding: 32, textAlign: 'center' }}>{loadError}</div>;
  if (!data) return <PageLoader fullPage />;

  return (
    <BookingScene
      booking={data.booking}
      form={form}
      onSubmitCode={handleSubmitCode}
      submitError={submitError}
      confirmed={confirmed}
      acceptingCounter={acceptingCounter}
      cancelling={cancelling}
      showCancelConfirm={showCancelConfirm}
      onAcceptCounter={handleAcceptCounter}
      onRequestCancel={() => setShowCancelConfirm(true)}
      onConfirmCancel={handleConfirmCancel}
      onDismissCancel={() => setShowCancelConfirm(false)}
    />
  );
}
